import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import budgetClientsApi, { BudgetClient } from '../../api/budgetClientsApi'
import { RootState } from '../rootState'
import { IPagination } from '../../types/pagination'

export interface ClientsState {
  clients: BudgetClient[]
  paginatedClients: IPagination<BudgetClient[]>
  loading: boolean
  error: string | null
  currentPage: number
  searchQuery: string
  sortField: string
  sortDirection: 'asc' | 'desc'
}

const initialState: ClientsState = {
  clients: [],
  paginatedClients: {
    count: 0,
    links: {
      next: null,
      previous: null
    },
    page_size: 10,
    results: [],
    current_page: 1,
    total_pages: 1,
    last_page: 1,
    from: 0,
    to: 0
  },
  loading: false,
  error: null,
  currentPage: 1,
  searchQuery: '',
  sortField: 'name',
  sortDirection: 'asc'
}

// Async thunks
export const fetchClients = createAsyncThunk(
  'clients/fetchClients',
  async () => {
    const clients = await budgetClientsApi.getClients()
    return clients
  }
)

export const createClient = createAsyncThunk(
  'clients/createClient',
  async (client: Partial<BudgetClient>) => {
    const newClient = await budgetClientsApi.createClient(client as BudgetClient)
    return newClient
  }
)

export const updateClient = createAsyncThunk(
  'clients/updateClient',
  async ({ id, data }: { id: number; data: Partial<BudgetClient> }) => {
    const updatedClient = await budgetClientsApi.updateClient(id, data)
    return updatedClient
  }
)

export const deleteClient = createAsyncThunk(
  'clients/deleteClient',
  async (id: number) => {
    await budgetClientsApi.deleteClient(id)
    return id
  }
)

export const fetchPaginatedClients = createAsyncThunk(
  'clients/fetchPaginatedClients',
  async (params: {
    page?: number
    page_size?: number
    search?: string
    order?: string
  }) => {
    const paginatedData = await budgetClientsApi.getPaginatedClients(params)
    return paginatedData
  }
)

// Slice
const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload
      state.currentPage = 1
    },
    setSortField: (state, action) => {
      state.sortField = action.payload
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClients.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false
        state.clients = action.payload
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch clients'
      })
      .addCase(createClient.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createClient.fulfilled, (state, action) => {
        state.loading = false
        state.clients.push(action.payload)
      })
      .addCase(createClient.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to create client'
      })
      .addCase(updateClient.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateClient.fulfilled, (state, action) => {
        state.loading = false
        const index = state.clients.findIndex(c => c.id === action.payload.id)
        if (index !== -1) {
          state.clients[index] = action.payload
        }
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update client'
      })
      .addCase(deleteClient.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.loading = false
        state.clients = state.clients.filter(c => c.id !== action.payload)
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete client'
      })
      .addCase(fetchPaginatedClients.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPaginatedClients.fulfilled, (state, action) => {
        state.loading = false
        state.paginatedClients = action.payload
      })
      .addCase(fetchPaginatedClients.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch clients'
      })
  }
})

export const { clearError, setCurrentPage, setSearchQuery, setSortField, setSortDirection } = clientsSlice.actions

// Selectors
export const selectClients = (state: RootState) => state.clients.clients
export const selectPaginatedClients = (state: RootState) => state.clients.paginatedClients
export const selectClientsLoading = (state: RootState) => state.clients.loading
export const selectClientsError = (state: RootState) => state.clients.error
export const selectCurrentPage = (state: RootState) => state.clients.currentPage
export const selectSearchQuery = (state: RootState) => state.clients.searchQuery
export const selectSortField = (state: RootState) => state.clients.sortField
export const selectSortDirection = (state: RootState) => state.clients.sortDirection

export default clientsSlice.reducer
